"use client";

import { c as _c } from "react/compiler-runtime";
import type {} from "react/canary";
import { ViewTransition, useSyncExternalStore, type ReactNode, type RefObject, type ViewTransitionInstance } from "react";
import { usePathname } from "next/navigation";
import "./page-transition.css";
import { useHistoryTransition } from "./useHistoryTransition";
import { DESKTOP_QUERY, readDesktopWidth, readServerDesktopWidth, subscribeToDesktopWidth } from "@/lib/breakpoints";

// The View Transition choreography below (snapshot geometry reads, Web
// Animations timing, the popstate replay in useHistoryTransition) is real
// main-thread work on every navigation. Desktop has CPU headroom to spare;
// low-end mobile devices don't, and a stalled/slow transition here shows up
// as a stuck-looking redirect or a frozen ghost frame of the outgoing page
// left composited over the header. Mobile skips all of it and gets Next's
// plain, instant client-side navigation instead.

// Small screens get a noticeably shorter transition — a full second reads as
// sluggish on a phone, where snappier navigation feedback is expected.
// Desktop's timing is unchanged.
const DESKTOP_DURATION = 1000;
const MOBILE_DURATION = 550;
const entranceDelay = 10;
const easing = "cubic-bezier(0.8, 0, 0.2, 1)";
function frames(entering: boolean): Keyframe[] {
  return entering ? [{
    opacity: 0,
    transform: "translateY(30vh)"
  }, {
    opacity: 1,
    transform: "translateY(0)"
  }] : [{
    opacity: 1,
    transform: "translateY(0) scale(1)"
  }, {
    opacity: 0,
    transform: "translateY(-10vh) scale(0.9)"
  }];
}
function animateSnapshot(name: string, entering: boolean) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const root = document.documentElement;
  const active = document.getAnimations().find(animation => animation.id.startsWith("dezine-page-") && animation.playState !== "finished");
  const startTime = typeof active?.startTime === "number" ? active.startTime : Number(document.timeline.currentTime ?? performance.now());
  const group = `::view-transition-group(${name})`;
  const style = getComputedStyle(root, group);
  const top = style.transform === "none" ? 0 : new DOMMatrixReadOnly(style.transform).m42;
  const height = Number.parseFloat(style.height);
  if (!Number.isFinite(top) || !Number.isFinite(height)) return;
  const viewportHeight = window.innerHeight;
  const isDesktopWidth = window.matchMedia(DESKTOP_QUERY).matches;
  const duration = isDesktopWidth ? DESKTOP_DURATION : MOBILE_DURATION;
  const options = {
    duration: duration + entranceDelay,
    fill: "both" as const
  };
  const snapshot = `::view-transition-${entering ? "new" : "old"}(${name})`;
  const animations: Animation[] = [];
  // Clipping to the content area below the header holds a static clip-path
  // for the transition's duration — skip it on small screens, where it's
  // extra compositing work for a correction that matters most on tall,
  // heavily-scrolled desktop layouts.
  if (isDesktopWidth) {
    const headerBottom = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
    const clipPath = `inset(${Math.max(0, headerBottom - top)}px 0 ${Math.max(0, height - (viewportHeight - top))}px)`;
    animations.push(root.animate([{
      clipPath
    }, {
      clipPath
    }], {
      ...options,
      pseudoElement: group
    }));
  }
  animations.push(root.animate([{
    transformOrigin: `50% ${viewportHeight / 2 - top}px`
  }], {
    ...options,
    pseudoElement: snapshot
  }), root.animate(frames(entering), {
    duration,
    delay: entering ? entranceDelay : 0,
    fill: "both",
    easing,
    pseudoElement: snapshot
  }));
  const mainAnimation = animations[animations.length - 1];
  mainAnimation.id = `dezine-page-${entering ? "enter" : "exit"}`;
  animations.forEach(animation => {
    animation.startTime = startTime;
  });
  return () => animations.forEach(animation => animation.cancel());
}
const enter = (instance: ViewTransitionInstance) => animateSnapshot(instance.name, true);
const exit = (instance: ViewTransitionInstance) => animateSnapshot(instance.name, false);
export default function PageTransition(t0) {
  const $ = _c(5);
  const {
    children,
    contentRef
  } = t0;
  const pathname = usePathname();
  const isDesktopWidth = useSyncExternalStore(subscribeToDesktopWidth, readDesktopWidth, readServerDesktopWidth);
  useHistoryTransition(pathname, contentRef, animateSnapshot, isDesktopWidth);
  if (!isDesktopWidth) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children || $[3] !== pathname) {
    t1 = <ViewTransition key={pathname} default="none" enter="dezine-route" exit="dezine-route" onEnter={enter} onExit={exit}>{children}</ViewTransition>;
    $[2] = children;
    $[3] = pathname;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}